import { useCallback, useRef, useState } from 'react';
import { normalizeMeteringTo01 } from './audioMetering';
import {
  inferVoiceUploadFormatFromRecorder,
  pickVoiceRecordingFormat,
} from './pickVoiceRecordingFormat';
import {
  VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS,
  VOICE_MESSAGE_WAVEFORM_PLAYBACK_BARS,
  downsampleMeteringSamplesToPeaks,
  resampleWaveformPeaks,
} from './voiceMessageWaveformPeaks';

/**
 * Voice recorder with Web Audio metering for waveform peaks.
 *
 * Web Audio AnalyserNode provides time-domain data (not dBFS directly).
 * We compute RMS from getFloatTimeDomainData, convert to dB (20 * log10(rms)),
 * then feed through normalizeMeteringTo01 — the same curve as RN expo-audio metering.
 *
 * stopRecording() returns Promise<{ blob, peaks, uploadMime, ext } | null>.
 * uploadMime / ext match API sign-url allowlist (no codec suffix on mime).
 */
const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformHeights, setWaveformHeights] = useState([]);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafIdRef = useRef(null);
  const timerRef = useRef(null);
  const dbSamplesRef = useRef([]);
  const resolveStopRef = useRef(null);
  /** Set when MediaRecorder starts; used to build File type + ext for upload */
  const voiceUploadFormatRef = useRef(null);
  /** While true, `dataavailable` rebuilds a blob URL for pause preview playback */
  const isPausedForPreviewRef = useRef(false);

  const [previewRecordingUrl, setPreviewRecordingUrl] = useState(null);
  const [previewWaveformHeights, setPreviewWaveformHeights] = useState([]);

  const stopMetering = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const sampleMetering = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);

    let sumSq = 0;
    for (let i = 0; i < buf.length; i += 1) {
      sumSq += buf[i] * buf[i];
    }
    const rms = Math.sqrt(sumSq / buf.length);
    const db = rms > 0 ? 20 * Math.log10(rms) : -160;

    dbSamplesRef.current.push(db);
    const h = normalizeMeteringTo01(db);
    setWaveformHeights(prev => [...prev.slice(-(VOICE_MESSAGE_WAVEFORM_DISPLAY_BARS - 1)), h]);

    rafIdRef.current = requestAnimationFrame(sampleMetering);
  }, []);

  const startRecording = useCallback(() => {
    if (mediaRecorderRef.current) return;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        dbSamplesRef.current = [];
        setWaveformHeights([]);
        setRecordingTime(0);
        setIsPaused(false);
        isPausedForPreviewRef.current = false;
        setPreviewRecordingUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setPreviewWaveformHeights([]);
        setIsRecording(true);

        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);
        audioContextRef.current = ctx;
        analyserRef.current = analyser;

        const picked = pickVoiceRecordingFormat();
        const recorder = picked
          ? new MediaRecorder(stream, { mimeType: picked.recorderMime })
          : new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];

        recorder.addEventListener('dataavailable', e => {
          if (e.data && e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
          if (isPausedForPreviewRef.current && chunksRef.current.length > 0) {
            const t =
              recorder.mimeType ||
              voiceUploadFormatRef.current?.recorderMime ||
              voiceUploadFormatRef.current?.uploadMime ||
              'audio/webm';
            try {
              const blob = new Blob(chunksRef.current, { type: t });
              if (blob.size > 0) {
                setPreviewRecordingUrl(prev => {
                  if (prev) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(blob);
                });
              }
            } catch {
              /* ignore */
            }
          }
        });

        recorder.addEventListener('stop', () => {
          stream.getTracks().forEach(t => t.stop());
          mediaRecorderRef.current = null;
          const fmt = voiceUploadFormatRef.current;
          const resolvedType =
            recorder.mimeType || fmt?.recorderMime || fmt?.uploadMime || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: resolvedType });
          chunksRef.current = [];
          const resolver = resolveStopRef.current;
          resolveStopRef.current = null;
          if (resolver) {
            resolver(blob);
          }
        });

        // Timeslice yields playable partial WebM in Chromium while paused; required for preview-before-send.
        const SLICE_MS = 200;
        try {
          recorder.start(SLICE_MS);
        } catch {
          recorder.start();
        }
        // After start(), `mimeType` is reliable (needed when the browser picks a default container).
        voiceUploadFormatRef.current = picked || inferVoiceUploadFormatFromRecorder(recorder);

        timerRef.current = setInterval(() => {
          setRecordingTime(t => t + 1);
        }, 1000);

        rafIdRef.current = requestAnimationFrame(sampleMetering);
      })
      .catch(err => {
        console.error('Voice recording failed:', err);
      });
  }, [sampleMetering]);

  /**
   * Stop recording and return { blob, peaks }.
   * @returns {Promise<{ blob: Blob, peaks: number[] }>}
   */
  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return Promise.resolve(null);

    stopMetering();

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const peaks = downsampleMeteringSamplesToPeaks([...dbSamplesRef.current]);
    isPausedForPreviewRef.current = false;
    setPreviewRecordingUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewWaveformHeights([]);

    return new Promise(resolve => {
      resolveStopRef.current = blob => {
        const fmt = voiceUploadFormatRef.current;
        voiceUploadFormatRef.current = null;
        const { uploadMime, ext } = fmt || inferVoiceUploadFormatFromRecorder(blob);
        resolve({ blob, peaks, uploadMime, ext });
      };
      try {
        if (recorder.state === 'paused' && typeof recorder.resume === 'function') {
          recorder.resume();
        }
        recorder.stop();
      } catch {
        resolveStopRef.current = null;
        voiceUploadFormatRef.current = null;
        chunksRef.current = [];
        isPausedForPreviewRef.current = false;
        setPreviewRecordingUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setPreviewWaveformHeights([]);
        setIsRecording(false);
        setIsPaused(false);
        setRecordingTime(0);
        resolve(null);
        return;
      }
      setIsRecording(false);
      setIsPaused(false);
      setRecordingTime(0);
    });
  }, [stopMetering]);

  const cancelRecording = useCallback(() => {
    resolveStopRef.current = null;
    voiceUploadFormatRef.current = null;
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      try {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      } catch {
        /* ignore */
      }
      mediaRecorderRef.current = null;
    }
    chunksRef.current = [];
    stopMetering();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setWaveformHeights([]);
    dbSamplesRef.current = [];
    isPausedForPreviewRef.current = false;
    setPreviewRecordingUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreviewWaveformHeights([]);
  }, [stopMetering]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    if (typeof recorder.pause !== 'function') return;
    try {
      const peaks = downsampleMeteringSamplesToPeaks([...dbSamplesRef.current]);
      setPreviewWaveformHeights(resampleWaveformPeaks(peaks, VOICE_MESSAGE_WAVEFORM_PLAYBACK_BARS));
      isPausedForPreviewRef.current = true;
      recorder.pause();
      setIsPaused(true);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      try {
        recorder.requestData();
      } catch {
        /* ignore */
      }
      setTimeout(() => {
        if (!isPausedForPreviewRef.current || !mediaRecorderRef.current) return;
        const r = mediaRecorderRef.current;
        if (r.state !== 'paused' || chunksRef.current.length === 0) return;
        const t =
          r.mimeType ||
          voiceUploadFormatRef.current?.recorderMime ||
          voiceUploadFormatRef.current?.uploadMime ||
          'audio/webm';
        try {
          const blob = new Blob(chunksRef.current, { type: t });
          if (blob.size > 0) {
            setPreviewRecordingUrl(prev => {
              if (prev) URL.revokeObjectURL(prev);
              return URL.createObjectURL(blob);
            });
          }
        } catch {
          /* ignore */
        }
      }, 0);
    } catch {
      /* ignore */
    }
  }, []);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    if (typeof recorder.resume !== 'function') return;
    try {
      isPausedForPreviewRef.current = false;
      setPreviewRecordingUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPreviewWaveformHeights([]);
      recorder.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      rafIdRef.current = requestAnimationFrame(sampleMetering);
    } catch {
      /* ignore */
    }
  }, [sampleMetering]);

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    isRecording,
    isPaused,
    recordingTime,
    waveformHeights,
    previewRecordingUrl,
    previewWaveformHeights,
  };
};

export default useVoiceRecorder;
